import React from 'react'
import { authService, autoSaveService } from '@oneblink/apps-react'
import { useHistory } from 'react-router-dom'
import styled from 'styled-components'
import { getDraftMeta } from '../../lib/drafts'
import {
  CenteredPageContent,
  LeftAlignedPageContent,
} from '../../components/LayoutComponents'
import { HTTPError } from '../../lib/http'
import LoadingSpinner from '../../components/LoadingSpinner'
import useQuery from '../../hooks/useQuery'
import useIsDraftInApprovals from '../../hooks/useIsDraftInApprovals'
import { useAppConfig } from '../../providers/AppConfigProvider'
import {
  getFormSlugOrId,
  useFormsDefinitions,
} from '../../providers/FormDefinitionsProvider'

interface FetchDraftState {
  fetchDraftMetaError?: HTTPError
  isFetchingDraftMeta: boolean
  draftDataUrl?: string
  formId?: number
}

const TextBox = styled.input`
  height: 4rem;
  border: unset;
  border-bottom: 1px solid rgba(0, 0, 0, 0.3);
  color: ${({ theme }) => theme.palette.nswBrandDark};
  line-height: 3rem;
  font-size: 3rem;
  max-width: 250px;
  &:focus {
    outline: none;
  }
`

const HeadingContainer = styled.div`
  width: 100%;
`

const ErrorText = styled.span`
  color: ${({ theme }) => theme.palette.nswStatusErrorRed};
  font-weight: 600;
`

const ButtonContainer = styled.div`
  margin-top: 1rem;
  margin-bottom: 1rem;
`

const Info = styled.p`
  margin-top: 2rem;
  margin-bottom: 2rem;
`

export default function ResumeDraft() {
  const history = useHistory()
  const { trackingCode: qsTrackingCode } = useQuery()
  const { formsOAuthClientId } = useAppConfig()

  const [fetchDraftState, setFetchDraftState] = React.useState<FetchDraftState>(
    { isFetchingDraftMeta: false },
  )

  const [isRedirecting, setRedirecting] = React.useState(false)

  const [trackingCode, setTrackingCode] = React.useState<string>('')

  const [
    { fetchErrorMessage: fetchIsDraftInApprovalsErrorMessage },
    fetchIsDraftInApprovals,
  ] = useIsDraftInApprovals()

  const { forms, isFetching: isFetchingForms } = useFormsDefinitions()

  const retrieveAndResumeDraft = React.useCallback(
    async (trackingCode: string) => {
      try {
        setFetchDraftState({ isFetchingDraftMeta: true })

        if (await fetchIsDraftInApprovals(trackingCode)) {
          throw new HTTPError(404, 'Error fetching draft')
        }

        const { draftDataUrl, formId } = await getDraftMeta(trackingCode)

        await autoSaveService.deleteAutoSaveData(formId, formId.toString())

        const form = forms.find((f) => f.id === formId)

        const slugOrId = form ? getFormSlugOrId(form) : formId

        if (draftDataUrl) {
          const pathname = `/forms/${slugOrId}`
          setRedirecting(true)
          history.push({
            pathname,
            search: `?trackingCode=${trackingCode}&draftDataUrl=${encodeURIComponent(
              draftDataUrl,
            )}`,
          })
        }
      } catch (error) {
        console.error(error)
        if (error instanceof HTTPError && error.status === 401) {
          authService.loginHostedUI(formsOAuthClientId)
          return
        }
        setFetchDraftState({
          isFetchingDraftMeta: false,
          fetchDraftMetaError:
            error instanceof HTTPError
              ? error
              : new HTTPError(500, 'Error fetching draft'),
        })
      }
    },
    [fetchIsDraftInApprovals, formsOAuthClientId, forms, history],
  )

  React.useEffect(() => {
    if (qsTrackingCode && typeof qsTrackingCode === 'string') {
      const trackingCodeFormatted = qsTrackingCode.substring(0, 6).toUpperCase()
      setTrackingCode(trackingCodeFormatted)
      if (trackingCodeFormatted.length === 6) {
        retrieveAndResumeDraft(trackingCodeFormatted)
      }
    }
  }, [qsTrackingCode, retrieveAndResumeDraft])

  const onChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const upperCased = e.currentTarget.value.toUpperCase().substring(0, 6)
      setTrackingCode(upperCased)
    },
    [],
  )

  const DraftNotFoundMessage = () => {
    if (fetchDraftState.fetchDraftMetaError?.status === 404) {
      return (
        <ErrorText>
          Saved form does not exist or has already been submitted
        </ErrorText>
      )
    }
    if (fetchDraftState.fetchDraftMetaError) {
      return <ErrorText>There was an error fetching the draft</ErrorText>
    }
    if (fetchIsDraftInApprovalsErrorMessage) {
      return <ErrorText>{fetchIsDraftInApprovalsErrorMessage}</ErrorText>
    }
    return (
      <ErrorText>Could not open saved form with the details provided</ErrorText>
    )
  }

  if (isFetchingForms) {
    return (
      <CenteredPageContent className="fa-resume-draft">
        <LoadingSpinner>Loading</LoadingSpinner>
      </CenteredPageContent>
    )
  }

  return (
    <LeftAlignedPageContent className="fa-resume-draft">
      <HeadingContainer className="fa-resume-draft__heading">
        <h2 className="title is-2 fa-resume-draft__heading-text">
          Open Your Saved Form
        </h2>
      </HeadingContainer>
      <Info className="fa-resume-draft__info">
        When you saved your form you should have been provided with a reference
        code.
        <br />
        To resume your form please complete the following details.
      </Info>
      <label className="ob-label fa-reference-code-label">Reference Code</label>
      <TextBox
        type="text"
        onChange={onChange}
        value={trackingCode}
        className="fa-reference-code-value"
        disabled={fetchDraftState.isFetchingDraftMeta}
      />
      <ButtonContainer className="fa-resume-draft-button-container">
        {fetchDraftState.isFetchingDraftMeta || isRedirecting ? (
          <LoadingSpinner>Loading form</LoadingSpinner>
        ) : (
          <button
            className="button is-primary fa-resume-draft-button"
            onClick={() => retrieveAndResumeDraft(trackingCode)}
            disabled={trackingCode.length !== 6}
          >
            <span className="fa-resume-draft-button__text">Resume</span>
          </button>
        )}
      </ButtonContainer>
      {(fetchDraftState.fetchDraftMetaError ||
        fetchIsDraftInApprovalsErrorMessage) && <DraftNotFoundMessage />}
    </LeftAlignedPageContent>
  )
}
